import React, { FC, useState, useEffect, useContext } from 'react';

import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Table from '@mui/material/Table';
import TableHead from '@mui/material/TableHead';
import TableCell from '@mui/material/TableCell';
import TableRow from '@mui/material/TableRow';
import TableBody from '@mui/material/TableBody';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';

import SelectInput from '../../../sharedModules/form/components/SelectInput';
import LoadingWrapper from '../../../sharedModules/layout/LoadingWrapper';

import ModuleRow from './ModuleRow';

import { SnackBarContext } from '../../../sharedModules/form/contexts/SnackBarContext';
import axios from '../../../sharedModules/utils/axios.utils';

interface ModuleAccessTableProps {
  userId: string;
  initialRoleName: string;
}

const tierOptions = ['No Access', 'Read-Only', 'Read/Write', 'Admin'];

const ModuleAccessTable: FC<ModuleAccessTableProps> = (props) => {
  const { userId, initialRoleName } = props;

  const { setSnackBarOpen, setSnackBarMessage } = useContext(SnackBarContext);

  const [appModulePermissions, setAppModulePermissions] = useState([]);
  const [permissionsLoading, setPermissionsLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [formValues, setFormValues] = useState<any>({});
  const [isExpanded, setIsExpanded] = useState(false);
  const [roleName, setRoleName] = useState(initialRoleName);

  const fetchAppModulePermissions = async () => {
    setPermissionsLoading(true);
    const response = await axios.get(`/admin/users/${userId}/app_module_permissions`);
    setAppModulePermissions((response.data as any).result);
    setFormValues((response.data as any).result);
    setPermissionsLoading(false);
  };

  useEffect(() => {
    if (userId) {
      fetchAppModulePermissions();
    }
  }, [userId]);

  useEffect(() => {
    setRoleName(initialRoleName);
  }, [initialRoleName]);

  const handleUpdatePermissions = async () => {
    setPermissionsLoading(true);

    try {
      await axios.post(`/admin/users/${userId}/app_module_permissions`, {
        permissions: formValues,
        user: { role_name: roleName },
      });
      fetchAppModulePermissions();
      setIsExpanded(true);
      setSnackBarOpen(true);
      setSnackBarMessage('Permissions Updated');
      setErrorMessage('');
    } catch {
      setErrorMessage('Something went wrong Updating Permissions');
    } finally {
      setPermissionsLoading(false);
    }
  };

  const handleRoleChange = (value: string) => {
    const buildPermissions = (predicate: (name: string) => number) =>
      appModulePermissions.map((m) => ({
        id: m.id,
        permission_tier: predicate(m.name),
      }));

    const permissionsByRole: Record<string, any> = {
      no_access: buildPermissions(() => 0),
      custom: buildPermissions(() => 0),
      platform_superadmin: buildPermissions(() => 3),
      sales_estimate_default_user: buildPermissions((name) => (name === 'Sales Estimate' ? 2 : 0)),
      chiller_logs_default_user: buildPermissions((name) => (name === 'Chiller Logs' ? 2 : 0)),
    };

    setRoleName(value);
    setFormValues(permissionsByRole[value]);
  };

  return (
    <LoadingWrapper isLoading={permissionsLoading}>
      <Accordion expanded={isExpanded} onChange={() => setIsExpanded(!isExpanded)}>
        <AccordionSummary>
          <Typography variant="h5" component="h5">
            Module Access
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Grid item xs={12} style={{ marginBottom: 20 }}>
            <SelectInput
              type="text"
              label="Select A Role"
              value={roleName}
              choices={[
                { label: 'No Access', value: 'no_access' },
                { label: 'Platform Superadmin', value: 'platform_superadmin' },
                { label: 'Sales Estimate Default User', value: 'sales_estimate_default_user' },
                { label: 'Chiller Logs Default User', value: 'chiller_logs_default_user' },
                { label: 'Custom', value: 'custom' },
              ]}
              valueChanged={(value) => handleRoleChange(value)}
            />
          </Grid>
          <Grid item xs={12}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>
                    <strong>Module</strong>
                  </TableCell>
                  {tierOptions?.map((tierOption) => (
                    <TableCell key={tierOption}>
                      <strong>{tierOption}</strong>
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {[...appModulePermissions]?.map((module) => {
                  return (
                    <ModuleRow
                      tierOptions={tierOptions}
                      module={module}
                      formValues={formValues}
                      setFormValues={setFormValues}
                      key={module.name}
                    />
                  );
                })}
              </TableBody>
            </Table>
          </Grid>
          <Grid item xs={12}>
            <Button variant="contained" color="primary" onClick={handleUpdatePermissions} style={{ marginTop: '20px' }}>
              Update Permissions
            </Button>
          </Grid>
        </AccordionDetails>
      </Accordion>
    </LoadingWrapper>
  );
};

export default ModuleAccessTable;
